// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import Material UI
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { createTheme, MuiThemeProvider, makeStyles } from '@material-ui/core/styles';

// Import Recoil
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { saveAvailable, baseOrigin, initialActions, changedFields, activeAutom, errorHandlingActiveAutom } from "../../../../recoil-store/recoil-state";
import FieldLabel from "../../../FlowConfigurationInputs/FieldLabel/FieldLabel";

// Import Helpers
import { CheckIfFlowUpdated } from '../../../../helpers/helper';
import { handleUpdate } from '../../../../helpers/flowHistory';
const useStyles = makeStyles({
    option: {
        '& > span': {
            marginRight: 10,
            fontSize: 12,
        },
        fontSize: 13,
        fontWeight: 600,
        color: '#46474E',
    }
});

/**
 * @Description This function lets you return component according to the name of the incoming action.
 * @Param       action - object
 * @Param       variables - object variable list
 * @Param       setOpenVariable - open variable function that allows us to open
 * @Returns     Component
 * @Author      Abdullah Onur Yeşil
 */
function GoTo({ action, isEhFlow, automDetail, setAutomDetail, showAlert, variables, setOpenVariable, recorder, variableRender }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const [selectedAction, setSelectedAction] = useState({});
    const base_origin = useRecoilValue(baseOrigin);
    const [mainAutomDetail, setMainAutomDetaill] = useRecoilState(activeAutom);
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const [flowActionList, setFlowActionList] = useState([]);
    const label = 'CONTENT.CONFIGURATION-FORM.ADVANCED-ACTIONS.GO-TO.';

    useEffect(() => {
        if (!action.fields?.destination) {
            action.fields.destination = { value: {}, type: 'ARRAY', isRequired: true };
        }

        setSelectedAction(action.fields.destination.value);
        let a = [];
        findActions(a, mainAutomDetail.items);
        setFlowActionList(a);
    }, [action?.id])

    const findActions = (a, items) => {
        return new Promise(async (resolve) => {
            for (const item of items) {
                if (!['START', 'TRIGGER-STATIC'].find(x => x === item.libraryName) && item.actionName !== 'GO-TO' && item.library_label !== 'REPEATABLE') {
                    if (item.library_label) {
                        let actionOrjName = item.library_label.includes('CUSTOM') ? item?.library_name + " - " + item.name :
                            (item.libraryName === 'STOP' ? '' : t(`CONTENT.LIBRARY.${item.libraryName}.NAME`) + " - ") +
                            t(`CONTENT.LIBRARY.${item.libraryName}.ACTION.${item.name}`);
                        let name = item.label ? item.label :
                            actionOrjName;
                        try {
                            // Checking if there is an item with the same name
                            const f = a.filter(x => x.name.includes(name))
                            if (f.length) {
                                let num = 1;
                                // found the largest number at the end of the name
                                for (const it of f) {
                                    const y = it.name.split(/[()]/)[1];
                                    if (y) {
                                        let n = parseInt(y)
                                        if (n > num) {
                                            num = n;
                                        }
                                    }
                                }
                                // number added to name
                                name += ` (${num + 1})`
                            }
                        } catch (error) {
                        }

                        a.push({ item_id: item.item_id, name, actionOrjName, image: item.image, image_path: item.image_path ? item.image_path : '' })
                    }

                    if (item.children) {
                        await findActions(a, item.children)
                    }
                }
            }
            resolve(true);
        })
    }

    const findAndUpdateItem = (items) => {
        items.forEach(item => {
            if (item.item_id === action.item_id) {
                item.fields = JSON.parse(JSON.stringify(action.fields))
                return;
            } else if (item.children) {
                findAndUpdateItem(item.children)
            }
        })
    }

    const changeGoToAction = (e, v) => {
        if (!v) {
            v = {};
        }
        setSelectedAction(v);
        action.fields.destination = { value: v, type: 'ARRAY', isRequired: true };
        let copyItems = JSON.parse(JSON.stringify(automDetail.items))

        const field = initialActionsData[action.id]?.fields.destination;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated("destination", action, oldValue, v, setSaveAvailable, setChangeFields, changedFieldsState);
        findAndUpdateItem(copyItems)
        handleUpdate(copyItems);
        setAutomDetail({
            ...automDetail,
            items: copyItems
        })
    }


    return (
        <div className="conf-field-container">
            <FieldLabel name={t(label + 'SELECT-DESTINATION')} is_required />
            <Autocomplete
                value={selectedAction}
                onChange={(e, newValue) => changeGoToAction(e, newValue)}
                id="size-small-outlined-multi"
                style={{ width: 300, position: 'relative', background: '#FBFBFB' }}
                size="small"
                options={flowActionList && flowActionList.length ? flowActionList.filter(x => !x.actionOrjName.includes('Advanced Actions - Go To')) : flowActionList}
                classes={{
                    option: classes.option,
                }}
                autoHighlight
                getOptionLabel={(option) => option.name}
                renderOption={(option) => (
                    <>
                        <img
                            loading="lazy"
                            width="20"
                            src={option.image_path ? `${base_origin}${option.image_path}` : option.image}
                            alt=""
                            style={{ marginRight: '10px' }}
                        />
                        {option.name}
                    </>
                )}
                renderInput={(params) => (
                    <>
                        {
                            selectedAction && selectedAction.image &&
                            <img className='input-action-icon' src={selectedAction.image_path ? `${base_origin}${selectedAction.image_path}` : selectedAction.image} />
                        }
                        <TextField
                            {...params}
                            className="select-action-input"
                            placeholder="No Action Selected"
                            variant="outlined"
                            classes={{
                                root: classes.resize
                            }}
                            inputProps={{
                                ...params.inputProps,
                                "aria-autocomplete": 'new-password',
                                autoComplete: 'off', // disable autocomplete and autofill
                                style: {
                                    marginLeft: '36px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#46474E'
                                }
                            }}
                        />
                    </>
                )}
            />
            <small className='conf-field-desc'><em>{t(label + 'SELECT-DESTINATION-DESC')}</em></small>
        </div>
    );
}

export default GoTo;
